import { loginInputSchema } from '@dovefiniscono/shared';
import { Link, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { errorMessage, oauthErrorMessages } from '../../lib/api';
import { type FieldErrors, validate } from '../../lib/form';
import { useSession } from '../../lib/session';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import styles from './Auth.module.css';
import { AuthLayout } from './AuthLayout';
import { ProviderButtons } from './ProviderButtons';

export function LoginPage() {
  const session = useSession();
  const search = useSearch({ strict: false }) as { error?: string; redirect?: string };
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(
    search.error
      ? (oauthErrorMessages[search.error] ?? oauthErrorMessages.oauth_failed ?? null)
      : null,
  );
  const [loading, setLoading] = useState(false);
  const expired = session.status === 'signedOut' && session.reason === 'expired';

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = validate(loginInputSchema, {
      email: form.get('email'),
      password: form.get('password'),
    });
    setErrors(result.errors ?? {});
    setFormError(null);
    if (!result.data) return;
    setLoading(true);
    try {
      await session.signIn(result.data);
    } catch (error) {
      setFormError(errorMessage(error));
      setLoading(false);
    }
  }

  return (
    <AuthLayout title="Accedi" subtitle="Riprendi da dove avevi lasciato.">
      {expired ? <p className={styles.notice}>La sessione è scaduta. Accedi di nuovo.</p> : null}
      <ProviderButtons returnTo={search.redirect} />
      {session.passwordSignIn ? (
        <>
          <form className={styles.fields} onSubmit={submit} noValidate>
            <TextField
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              error={errors.email}
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              error={errors.password}
            />
            {formError ? (
              <p className={styles.notice} role="alert">
                {formError}
              </p>
            ) : null}
            <Button type="submit" variant="primary" className={styles.submit} loading={loading}>
              Accedi
            </Button>
          </form>
          <div className={styles.links}>
            <Link to="/password-dimenticata">Password dimenticata?</Link>
            <span>
              Non hai un account? <Link to="/registrati">Registrati</Link>
            </span>
          </div>
        </>
      ) : session.passwordSignIn === false ? (
        <p className={styles.links}>
          Non hai un account? Si crea da solo la prima volta che entri.
        </p>
      ) : null}
    </AuthLayout>
  );
}
